import React, { useState, useEffect } from 'react';
import { getAccounts, approveAccount, waitlistAccount, suspendAccount, updateSubscription } from '../services/adminService';
import { getAnalyticsSummary } from '../services/analyticsService';

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'waitlisted', label: 'Waitlisted' },
  { key: 'suspended', label: 'Suspended' },
];

const statusBadge = (status) => {
  switch (status) {
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'approved': return 'bg-green-100 text-green-800';
    case 'waitlisted': return 'bg-blue-100 text-blue-800';
    case 'suspended': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('accounts');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);

  useEffect(() => {
    fetchAccounts();
  }, [statusFilter]);

  useEffect(() => {
    if (activeTab === 'analytics') fetchAnalytics();
  }, [activeTab, analyticsDays]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAccounts(statusFilter || undefined);
      setAccounts(data.accounts || []);
    } catch (err) {
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const data = await getAnalyticsSummary(analyticsDays);
      setAnalytics(data);
    } catch (err) {
      setError('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleAction = async (accountId, action, label) => {
    if (action === 'suspend' && !window.confirm('Suspend this account? They will lose access immediately.')) return;
    try {
      setActionLoading(accountId);
      if (action === 'approve') await approveAccount(accountId);
      else if (action === 'waitlist') await waitlistAccount(accountId);
      else if (action === 'suspend') await suspendAccount(accountId);
      fetchAccounts();
    } catch (err) {
      setError(`Failed to ${label} account`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubscription = async (accountId, data) => {
    try {
      await updateSubscription(accountId, data);
      fetchAccounts();
    } catch (err) {
      setError('Failed to update subscription');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const pendingCount = accounts.filter(a => a.status === 'pending').length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin</h1>
        <p className="mt-1 text-sm text-gray-500">Manage accounts and view analytics</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}

      {/* Top-level tabs: Accounts | Analytics */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-6">
          <button
            onClick={() => setActiveTab('accounts')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'accounts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Accounts
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'analytics'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Analytics
          </button>
        </div>
      </div>

      {activeTab === 'accounts' && (
        <AccountsTab
          accounts={accounts}
          loading={loading}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          pendingCount={pendingCount}
          actionLoading={actionLoading}
          handleAction={handleAction}
          handleSubscription={handleSubscription}
          formatDate={formatDate}
        />
      )}

      {activeTab === 'analytics' && (
        <AnalyticsTab
          analytics={analytics}
          loading={analyticsLoading}
          days={analyticsDays}
          setDays={setAnalyticsDays}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};

/* ─── Accounts Tab ─── */
const AccountsTab = ({ accounts, loading, statusFilter, setStatusFilter, pendingCount, actionLoading, handleAction, handleSubscription, formatDate }) => (
  <>
    {/* Status tabs — dropdown on mobile, buttons on desktop */}
    <div className="sm:hidden">
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium bg-white"
      >
        {STATUS_TABS.map(tab => (
          <option key={tab.key} value={tab.key}>
            {tab.label}{tab.key === 'pending' && pendingCount > 0 ? ` (${pendingCount})` : ''}
          </option>
        ))}
      </select>
    </div>
    <div className="hidden sm:flex flex-wrap gap-2">
      {STATUS_TABS.map(tab => (
        <button
          key={tab.key}
          onClick={() => setStatusFilter(tab.key)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === tab.key
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {tab.label}
          {tab.key === 'pending' && pendingCount > 0 && statusFilter !== 'pending' && (
            <span className="ml-1.5 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
              {pendingCount}
            </span>
          )}
        </button>
      ))}
    </div>

    {loading ? (
      <div className="text-center py-8 text-gray-500">Loading...</div>
    ) : accounts.length === 0 ? (
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
        No {statusFilter || ''} accounts found.
      </div>
    ) : (
      <div className="space-y-3">
        {accounts.map(account => {
          const owner = account.members?.find(m => m.role === 'owner');
          const memberCount = account.members?.length || 0;
          const isActioning = actionLoading === account.id;

          return (
            <div key={account.id} className="bg-white rounded-lg shadow p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-semibold text-gray-900">{account.business_name}</h3>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusBadge(account.status)}`}>
                      {account.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1 space-y-0.5">
                    {owner && (
                      <p>
                        Owner: <span className="font-medium">{owner.user?.display_name || 'Unknown'}</span>
                        {owner.user?.phone_number && (
                          <span className="text-gray-400 ml-2">{owner.user.phone_number}</span>
                        )}
                      </p>
                    )}
                    <p>
                      {memberCount} member{memberCount !== 1 ? 's' : ''}
                      {account.location_count > 0 && (
                        <span className="ml-3">{account.location_count} location{account.location_count !== 1 ? 's' : ''}</span>
                      )}
                    </p>
                    <p className="text-gray-400 text-xs">
                      Signed up {formatDate(account.created_at)}
                      {owner?.user?.last_login_at && (
                        <span className="ml-3">Last login {formatDate(owner.user.last_login_at)}</span>
                      )}
                    </p>
                    {account.status === 'approved' && (
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          account.subscription_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {account.subscription_status === 'trialing'
                            ? (account.trial_days_remaining != null ? `Trial: ${account.trial_days_remaining}d left` : 'Beta (no expiry)')
                            : account.subscription_status}
                        </span>
                        {account.subscription_status === 'trialing' && !account.trial_ends_at && (
                          <button
                            onClick={() => {
                              const days = prompt('Set trial length (days from today):');
                              if (days && !isNaN(days)) {
                                const end = new Date(Date.now() + Number(days) * 86400000).toISOString();
                                handleSubscription(account.id, { trial_ends_at: end });
                              }
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                          >
                            Set trial end
                          </button>
                        )}
                        {account.subscription_status === 'trialing' && account.trial_ends_at && (
                          <button
                            onClick={() => handleSubscription(account.id, { trial_ends_at: null })}
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                          >
                            Remove expiry
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex flex-wrap gap-2">
                  {account.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleAction(account.id, 'approve', 'approve')}
                        disabled={isActioning}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleAction(account.id, 'waitlist', 'waitlist')}
                        disabled={isActioning}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
                      >
                        Waitlist
                      </button>
                    </>
                  )}
                  {account.status === 'waitlisted' && (
                    <button
                      onClick={() => handleAction(account.id, 'approve', 'approve')}
                      disabled={isActioning}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
                    >
                      Approve
                    </button>
                  )}
                  {account.status === 'suspended' && (
                    <button
                      onClick={() => handleAction(account.id, 'approve', 'reactivate')}
                      disabled={isActioning}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
                    >
                      Reactivate
                    </button>
                  )}
                  {account.status === 'approved' && (
                    <button
                      onClick={() => handleAction(account.id, 'suspend', 'suspend')}
                      disabled={isActioning}
                      className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
                    >
                      Suspend
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </>
);

/* ─── Analytics Tab ─── */
const AnalyticsTab = ({ analytics, loading, days, setDays, formatDate }) => {
  if (loading) return <div className="text-center py-8 text-gray-500">Loading analytics...</div>;
  if (!analytics) return null;

  const { logins, sessions, platforms, page_views, feature_usage } = analytics;
  const totalPlatform = (platforms.mobile || 0) + (platforms.desktop || 0);
  const mobilePct = totalPlatform > 0 ? Math.round((platforms.mobile / totalPlatform) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center gap-3">
        <label className="text-sm text-gray-600">Period:</label>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="border border-gray-300 rounded-md px-2 py-1 text-sm"
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard label="Total Logins" value={logins.total} />
        <SummaryCard label="Unique Users" value={logins.unique_users} />
        <SummaryCard label="Sessions" value={sessions.total} />
        <SummaryCard label="Avg Session" value={`${sessions.avg_duration_min}m`} />
        <SummaryCard label="Median Session" value={`${sessions.median_duration_min}m`} />
        <SummaryCard label="Mobile" value={`${mobilePct}%`} sub={`${platforms.mobile || 0} sessions`} />
        <SummaryCard label="Desktop" value={`${100 - mobilePct}%`} sub={`${platforms.desktop || 0} sessions`} />
      </div>

      {/* Logins by day chart (simple bar representation) */}
      {logins.by_day.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Logins by Day</h3>
          <div className="space-y-1">
            {logins.by_day.map(d => {
              const maxCount = Math.max(...logins.by_day.map(x => x.count));
              const pct = maxCount > 0 ? (d.count / maxCount) * 100 : 0;
              return (
                <div key={d.day} className="flex items-center gap-2 text-xs">
                  <span className="w-16 sm:w-20 text-gray-500 shrink-0">{d.day.slice(5)}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                    <div className="bg-blue-500 h-full rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-6 text-right text-gray-700 font-medium">{d.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top pages */}
      {page_views.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Top Pages</h3>
          <div className="space-y-1">
            {page_views.slice(0, 10).map(pv => (
              <div key={pv.page} className="flex items-center justify-between text-sm py-1">
                <span className="text-gray-700 truncate flex-1">{pv.page || '/'}</span>
                <span className="text-gray-500 ml-2 font-medium">{pv.views}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Feature usage per account */}
      {feature_usage.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Feature Usage by Account</h3>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase border-b">
                  <th className="pb-2 px-3 font-medium">Account</th>
                  <th className="pb-2 px-2 font-medium text-center">Users</th>
                  <th className="pb-2 px-2 font-medium text-center">Sessions</th>
                  <th className="pb-2 px-2 font-medium text-center">Cash Outs</th>
                  <th className="pb-2 px-2 font-medium text-center">Soap Changes</th>
                  <th className="pb-2 px-2 font-medium text-center">Work Orders</th>
                  <th className="pb-2 px-2 font-medium text-center">CC Uploads</th>
                  <th className="pb-2 px-2 font-medium text-center">Bills</th>
                  <th className="pb-2 px-2 font-medium">Last Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {feature_usage.map(row => (
                  <tr key={row.account_id} className="hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium text-gray-900 whitespace-nowrap">{row.business_name}</td>
                    <td className="py-2 px-2 text-center text-gray-600">{row.active_users}</td>
                    <td className="py-2 px-2 text-center text-gray-600">{row.sessions}</td>
                    <td className="py-2 px-2 text-center text-gray-600">{row.cashouts || 0}</td>
                    <td className="py-2 px-2 text-center text-gray-600">{row.soap_changes || 0}</td>
                    <td className="py-2 px-2 text-center text-gray-600">{row.work_orders || 0}</td>
                    <td className="py-2 px-2 text-center text-gray-600">{row.cc_uploads || 0}</td>
                    <td className="py-2 px-2 text-center text-gray-600">{row.bill_uploads || 0}</td>
                    <td className="py-2 px-2 text-gray-500 whitespace-nowrap">{formatDate(row.last_active)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, sub }) => (
  <div className="bg-white rounded-lg shadow p-3 sm:p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

export default AdminDashboard;
